import React, { useEffect } from 'react';
import {
  RestaurantDetails_getRestaurantDetails,
  RestaurantDetails_getRestaurantDetails_awards
} from '../../../../../types/graphql-types-generated.ts';
import { Icon } from '../../../../../patterns/00-atoms/icon/icon.tsx';
import { OpeningTimes, openingTimesEmpty } from './opening-times.tsx';
import { pluralize } from '../../../../../utils/string.ts';
import { Button, TextIconButton } from '../../../../../patterns/00-atoms/button/button.tsx';
import { Paths } from '../../../../../utils/paths.ts';
import {
  FacebookLogo,
  InstagramLogo
} from '../../../../../patterns/00-atoms/social-media-logos/social-media-logos.tsx';
import { BrandSkdjSymbol } from '../../../../../patterns/00-atoms/brand/brand-skdj.tsx';
import { repairUrlProtocol } from '../../../../../utils/url.ts';
import { hasReservation } from '../../../../../utils/reservation.ts';
import { prgLink } from '../../../../../utils/prg-resolver.ts';
import { UBTracking } from '../../../../../components/tracking/ubtracking.ts';
import { SiblingRestaurants } from '../../01-molecules/sibling-restaurants/sibling-restaurants.tsx';
import { nonNullElement } from '../../../../../utils/non-null-element.ts';

export type RestaurantInfoModalOverviewProps = {
  restaurantDetails: RestaurantDetails_getRestaurantDetails;
  onMapClick: () => void;
  onReserveClick: () => void;
  onReviewsClick: () => void;
  onMapLoad?: (target: any) => void;
  onReserveLoad?: (target: any) => void;
  onReviewsLoad?: (target: any) => void;
};

export const RestaurantInfoModalOverview: React.FunctionComponent<
  RestaurantInfoModalOverviewProps
> = props => {
  const { restaurantDetails } = props;
  const {
    keyname,
    street,
    streetnumber,
    linkedCity,
    phone,
    phoneprefix,
    mainOpeningTimes,
    kitchenOpeningTimes,
    averageReviewScore,
    reviewsCount,
    description,
    website,
    instagramUrl,
    facebookUrl,
    services,
    awards,
    name
  } = restaurantDetails;
  const feedbackButtonRef = React.useRef<HTMLAnchorElement>(null);

  const hasMainOpeningTimes = !openingTimesEmpty(mainOpeningTimes);
  const hasKitchenOpeningTimes = !openingTimesEmpty(kitchenOpeningTimes);

  const overviewRow = (options: {
    iconName: string;
    content: React.ReactNode;
    button?: { text: string; onClick: () => void; onLoad?: (target: HTMLDivElement) => void };
  }) => {
    const buttonContainerRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (options.button?.onLoad && buttonContainerRef.current) {
        options.button.onLoad(buttonContainerRef.current);
      }
    }, [options.button]);

    return (
      <div className="flex items-center gap-3 py-4">
        <Icon name={options.iconName} className="text-2xl" />
        <div className="text-sm">{options.content}</div>
        {options.button && (
          <div className="ml-auto" ref={buttonContainerRef}>
            <TextIconButton
              align="left"
              iconName="arrow-right"
              onClick={options.button.onClick}
              className="text-right"
            >
              {options.button.text}
            </TextIconButton>
          </div>
        )}
      </div>
    );
  };

  const aboutUs = (
    <div className="mb-8">
      <h3 className="mb-2 text-xl font-extrabold">Über uns</h3>
      <div className="mb-4">{description}</div>
      <div className="flex justify-start">
        {website && (
          <a
            href={repairUrlProtocol(website)}
            className="text-sk-red-500 mr-4 flex items-center text-sm"
            onClick={() =>
              UBTracking.trackEvent.click('restaurant_info_modal', 'social_media', 'website')
            }
          >
            <Icon name="website text-2xl mr-1" />
            Website
          </a>
        )}
        {facebookUrl && (
          <FacebookLogo
            variant="red"
            className="mr-4"
            onClick={() =>
              UBTracking.trackEvent.click('restaurant_info_modal', 'social_media', 'facebook')
            }
            onLoad={target =>
              UBTracking.onTrackTargetView(
                target,
                'restaurant_info_modal',
                'social_media',
                'facebook'
              )
            }
          />
        )}
        {instagramUrl && (
          <InstagramLogo
            variant="red"
            onClick={() =>
              UBTracking.trackEvent.click('restaurant_info_modal', 'social_media', 'instagram')
            }
            onLoad={target =>
              UBTracking.onTrackTargetView(
                target,
                'restaurant_info_modal',
                'social_media',
                'instagram'
              )
            }
          />
        )}
      </div>
    </div>
  );

  const awardEntry = (
    award: RestaurantDetails_getRestaurantDetails_awards | null,
    index: number
  ) => {
    if (award?.type === 'SKDJ_YEAR') {
      return (
        <div key={`${award.type}-${index}`} className="mb-2 flex items-center">
          <BrandSkdjSymbol />
          <span className="text-sk-dark-blue font-bold">{award.title}</span>
        </div>
      );
    } else {
      /* TODO entries for "Varta" and “Der große Guide" */
      return <></>;
    }
  };

  const siblingRestaurants = restaurantDetails.owner?.claims
    .filter(nonNullElement)
    .filter(claim => claim.id !== restaurantDetails.id);

  useEffect(() => {
    if (feedbackButtonRef.current) {
      UBTracking.onTrackTargetView(
        feedbackButtonRef.current,
        'restaurant_info_modal',
        'feedback_button'
      );
    }
  });

  return (
    <>
      <div className="divide-sk-grey-200 divide-y-[1px]">
        {overviewRow({
          iconName: 'location',
          content: (
            <>
              <div>
                <strong>{name}</strong>
              </div>
              <div>
                {street} {streetnumber}
              </div>
              <div>{linkedCity.name}</div>
            </>
          ),
          button: {
            text: 'Karte anzeigen',
            onClick: props.onMapClick,
            onLoad: props.onMapLoad
          }
        })}
        {(hasMainOpeningTimes || hasKitchenOpeningTimes) &&
          overviewRow({
            iconName: 'opening-hours',
            content: (
              <>
                {hasMainOpeningTimes && mainOpeningTimes && (
                  <>
                    <div>
                      <h3 className="font-bold">Öffnungszeiten von {name}:</h3>
                    </div>
                    <OpeningTimes openingTimes={mainOpeningTimes} />
                  </>
                )}
                {hasKitchenOpeningTimes && kitchenOpeningTimes && (
                  <>
                    <div className="mt-2">
                      <strong>Küche:</strong>
                    </div>
                    <OpeningTimes openingTimes={kitchenOpeningTimes} />
                  </>
                )}
              </>
            )
          })}
        {phone &&
          overviewRow({
            iconName: 'phone',
            content: (
              <>
                <div>
                  <h3 className="font-bold">Telefon</h3>
                </div>
                {phoneprefix && <>{phoneprefix}/</>}
                {phone}
              </>
            ),
            button: hasReservation(props.restaurantDetails)
              ? {
                  text: 'Online-Reservierung',
                  onClick: props.onReserveClick,
                  onLoad: props.onReserveLoad
                }
              : undefined
          })}
        {overviewRow({
          iconName: 'rating-empty',
          content: (
            <>
              <div>
                <strong>{averageReviewScore.toFixed(1)}</strong>
              </div>
              <span className="whitespace-nowrap">
                {reviewsCount} {pluralize(reviewsCount, 'Bewertung', 'Bewertungen')}
              </span>
            </>
          ),
          button: {
            text: reviewsCount > 0 ? 'Alle anzeigen' : 'Bewertung schreiben',
            onClick: props.onReviewsClick,
            onLoad: props.onReviewsLoad
          }
        })}
      </div>
      <hr className="text-sk-grey-200 mt-4 -mr-4 mb-8 -ml-4 border-2 pr-4 pl-4" />
      {aboutUs}
      {services.length > 0 && (
        <div className="mb-3">
          <h2 className="mb-3 text-xl font-extrabold">Services</h2>
          <div>
            {services.map(
              (service, index) =>
                service && (
                  <span
                    key={`${service.id}-${index}`}
                    className="bg-sk-grey-200 mr-3 mb-2.5 inline-block rounded-[20px] px-2.5 py-1 text-center align-baseline"
                  >
                    {service.name}
                  </span>
                )
            )}
          </div>
        </div>
      )}
      {awards &&
        /* TODO entries for "Varta" and “Der große Guide" */
        awards.find(a => a?.type === 'SKDJ_YEAR') && (
          <div className="mb-3">
            <h3 className="mb-3 text-xl font-extrabold">Auszeichnungen</h3>
            {awards.map(awardEntry)}
          </div>
        )}
      {siblingRestaurants && siblingRestaurants.length > 0 && (
        <SiblingRestaurants restaurantDetails={restaurantDetails} siblings={siblingRestaurants} />
      )}
      <hr className="text-sk-grey-200 mt-4 -mr-4 mb-8 -ml-4 border-2 pr-4 pl-4" />
      <div className="text-sk-dark-blue text-center">
        <div className="font-bold">Du hast einen Fehler gefunden?</div>
        <div className="mb-3">Wir freuen uns über dein Feedback!</div>
        <Button
          ref={feedbackButtonRef}
          className="mb-2"
          variant="dark"
          onClick={() => {
            UBTracking.state.restaurant = props.restaurantDetails;
            UBTracking.trackEvent.click('restaurant_info_modal', 'feedback_button');
            prgLink(Paths.restaurantFeedback(linkedCity.fullnameLower, keyname));
          }}
        >
          Feedback geben
        </Button>
      </div>
    </>
  );
};
